import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useCurrentUser } from "@/lib/auth";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { canWriteStammdaten } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { fetchWebsiteNews, updateWebsiteNews } from "@/lib/websiteNewsApi";
import {
  buildWebsiteNewsPreview,
  classifyWebsiteNewsDisplayStatus,
  formatWebsitePreviewDate,
  supersededByTitle,
  websiteDisplayStatusClass,
  websiteDisplayStatusLabel,
} from "@/lib/websiteNewsStatus";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import type { WebsiteNewsItem, WebsiteNewsScheduleMode, WebsiteNewsWebsitePreview } from "@/types/websiteNews";

function newId(): string {
  return crypto.randomUUID();
}

function newWebsiteNewsItem(scheduleMode: WebsiteNewsScheduleMode = "always"): WebsiteNewsItem {
  const base: WebsiteNewsItem = {
    id: newId(),
    locale: "de",
    title: "",
    body: "",
    signature: "",
    sort_order: 10,
    is_active: true,
    schedule_mode: scheduleMode,
  };
  if (scheduleMode === "recurring") {
    return {
      ...base,
      start_month: 4,
      start_day: 1,
      end_month: 9,
      end_day: 30,
    };
  }
  if (scheduleMode === "fixed") {
    return {
      ...base,
      valid_from: null,
      valid_until: null,
    };
  }
  return base;
}

function scheduleLabel(item: WebsiteNewsItem): string {
  if (item.schedule_mode === "always") return "Immer sichtbar";
  if (item.schedule_mode === "fixed") {
    const from = item.valid_from ? formatIsoDateShort(item.valid_from) : "…";
    const until = item.valid_until ? formatIsoDateShort(item.valid_until) : "…";
    return `${from} – ${until}`;
  }
  const from = item.start_day && item.start_month ? `${item.start_day}.${item.start_month}.` : "…";
  const until = item.end_day && item.end_month ? `${item.end_day}.${item.end_month}.` : "…";
  return `Jährlich ${from} – ${until}`;
}

export function WebsiteNewsPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteStammdaten(groups);

  const [items, setItems] = useState<WebsiteNewsItem[]>([]);
  const [websitePreview, setWebsitePreview] = useState<WebsiteNewsWebsitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWebsiteNews();
      setItems(data.items);
      setWebsitePreview(data.websitePreview);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPageMeta({
      title: "Website-News",
      subtitle: "Meldungen auf der Campingplatz-Website verwalten",
    });
    void load();
    return () => setPageMeta(null);
  }, [load, setPageMeta]);

  function patchItem(id: string, patch: Partial<WebsiteNewsItem>) {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
    setSuccess(false);
  }

  function removeItem(id: string) {
    setItems((prev) => prev.filter((item) => item.id !== id));
    setSuccess(false);
  }

  async function onSave() {
    if (!canEdit) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const saved = await updateWebsiteNews(items);
      setItems(saved.items);
      setWebsitePreview(saved.websitePreview);
      setSuccess(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  const sortedItems = useMemo(
    () => items.slice().sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title)),
    [items],
  );

  const preview = useMemo(() => buildWebsiteNewsPreview(items), [items]);

  const previewByLocale = useMemo(
    () => Object.fromEntries(preview.active.map((entry) => [entry.locale, entry])),
    [preview],
  );

  return (
    <div className="space-y-6">
      <p className="text-sm text-label-secondary">
        Auf der Website wird immer nur eine Meldung angezeigt – die aktivste mit der niedrigsten
        Sortierung. Mehrere Meldungen können sich über Zeiträume abwechseln, z.&nbsp;B. Winterschlaf
        im Winter und Saison-Hinweise im Sommer.
      </p>

      {!loading ? (
        <Card title="Aktuell auf der Website">
          <p className="text-sm text-label-secondary">
            Stand: {formatWebsitePreviewDate(preview.reference_date)}
          </p>
          <ul className="mt-3 space-y-2 text-sm">
            {preview.active.map((entry) => (
              <li key={entry.locale}>
                <span className="font-medium uppercase">{entry.locale}:</span>{" "}
                {entry.title ? (
                  <span>{entry.title}</span>
                ) : (
                  <span className="text-status-amber">Keine Meldung wird angezeigt</span>
                )}
              </li>
            ))}
          </ul>
          {!previewByLocale.de?.title && !previewByLocale.en?.title ? (
            <p className="mt-3 rounded-lg border border-status-amber/40 bg-status-amber/10 px-3 py-2 text-sm text-status-amber">
              Für heute ist keine Meldung aktiv. Prüfen Sie „Aktiv“, den Anzeigezeitraum und die
              Sortierung.
            </p>
          ) : null}
          {websitePreview &&
          (websitePreview.active.some(
            (entry, index) =>
              entry.item_id !== preview.active[index]?.item_id ||
              entry.title !== preview.active[index]?.title,
          ) ||
            websitePreview.reference_date !== preview.reference_date) ? (
            <p className="mt-3 text-xs text-label-secondary">
              Die Vorschau oben berücksichtigt Ihre aktuellen Eingaben. Nach dem Speichern wird die
              Website entsprechend aktualisiert.
            </p>
          ) : null}
        </Card>
      ) : null}

      {!canEdit ? <StammdatenWriteHint /> : null}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {success ? <p className="text-sm text-status-green">Gespeichert.</p> : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Laden…</p>
      ) : (
        <div className="space-y-4">
          {sortedItems.map((item) => {
            const displayStatus = classifyWebsiteNewsDisplayStatus(item, items, new Date());
            const supersededBy = supersededByTitle(item, items, new Date());
            return (
            <Card
              key={item.id}
              title={item.title || "Neue Meldung"}
              actions={
                <span
                  className={`rounded-full border px-2.5 py-1 text-xs font-medium ${websiteDisplayStatusClass(displayStatus)}`}
                >
                  {websiteDisplayStatusLabel(displayStatus)}
                </span>
              }
            >
              <div className="space-y-4">
                {displayStatus === "superseded" && supersededBy ? (
                  <p className="text-sm text-status-amber">
                    Stattdessen wird angezeigt: „{supersededBy}“ (niedrigere Sortierung)
                  </p>
                ) : null}
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="flex flex-col gap-1 text-sm">
                    Titel
                    <input
                      className="eh-input"
                      disabled={!canEdit}
                      value={item.title}
                      onChange={(e) => patchItem(item.id, { title: e.target.value })}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    Sprache
                    <select
                      className="eh-input"
                      disabled={!canEdit}
                      value={item.locale}
                      onChange={(e) =>
                        patchItem(item.id, { locale: e.target.value as WebsiteNewsItem["locale"] })
                      }
                    >
                      <option value="de">Deutsch</option>
                      <option value="en">Englisch</option>
                    </select>
                  </label>
                </div>

                <label className="flex flex-col gap-1 text-sm">
                  Text
                  <textarea
                    className="eh-input min-h-32"
                    disabled={!canEdit}
                    value={item.body}
                    onChange={(e) => patchItem(item.id, { body: e.target.value })}
                  />
                </label>

                <div className="grid gap-4 md:grid-cols-2">
                  <label className="flex flex-col gap-1 text-sm">
                    Unterschrift
                    <input
                      className="eh-input"
                      disabled={!canEdit}
                      value={item.signature}
                      onChange={(e) => patchItem(item.id, { signature: e.target.value })}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    Sortierung
                    <input
                      type="number"
                      className="eh-input w-28"
                      disabled={!canEdit}
                      value={item.sort_order}
                      onChange={(e) =>
                        patchItem(item.id, { sort_order: Number(e.target.value) || 0 })
                      }
                    />
                  </label>
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    disabled={!canEdit}
                    checked={item.is_active}
                    onChange={(e) => patchItem(item.id, { is_active: e.target.checked })}
                  />
                  Aktiv
                </label>

                <fieldset className="space-y-3 rounded-lg border border-separator p-4">
                  <legend className="px-1 text-sm font-medium">Anzeigezeitraum</legend>
                  <p className="text-xs text-label-secondary">
                    Zeitraum: {scheduleLabel(item)}
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    {(
                      [
                        ["always", "Immer"],
                        ["recurring", "Jährlich wiederkehrend"],
                        ["fixed", "Fester Zeitraum"],
                      ] as const
                    ).map(([mode, label]) => (
                      <label key={mode} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name={`schedule-${item.id}`}
                          disabled={!canEdit}
                          checked={item.schedule_mode === mode}
                          onChange={() => {
                            const next = newWebsiteNewsItem(mode);
                            patchItem(item.id, {
                              schedule_mode: mode,
                              valid_from: next.valid_from,
                              valid_until: next.valid_until,
                              start_month: next.start_month,
                              start_day: next.start_day,
                              end_month: next.end_month,
                              end_day: next.end_day,
                            });
                          }}
                        />
                        {label}
                      </label>
                    ))}
                  </div>

                  {item.schedule_mode === "fixed" ? (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="flex flex-col gap-1 text-sm">
                        Von
                        <input
                          type="date"
                          className="eh-input"
                          disabled={!canEdit}
                          value={item.valid_from ?? ""}
                          onChange={(e) =>
                            patchItem(item.id, { valid_from: e.target.value || null })
                          }
                        />
                      </label>
                      <label className="flex flex-col gap-1 text-sm">
                        Bis
                        <input
                          type="date"
                          className="eh-input"
                          disabled={!canEdit}
                          value={item.valid_until ?? ""}
                          onChange={(e) =>
                            patchItem(item.id, { valid_until: e.target.value || null })
                          }
                        />
                      </label>
                    </div>
                  ) : null}

                  {item.schedule_mode === "recurring" ? (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="flex flex-col gap-1 text-sm">
                        Von (Tag.Monat)
                        <div className="flex gap-2">
                          <input
                            type="number"
                            className="eh-input w-20"
                            min={1}
                            max={31}
                            disabled={!canEdit}
                            value={item.start_day ?? ""}
                            onChange={(e) =>
                              patchItem(item.id, { start_day: Number(e.target.value) || null })
                            }
                            aria-label="Starttag"
                          />
                          <input
                            type="number"
                            className="eh-input w-20"
                            min={1}
                            max={12}
                            disabled={!canEdit}
                            value={item.start_month ?? ""}
                            onChange={(e) =>
                              patchItem(item.id, { start_month: Number(e.target.value) || null })
                            }
                            aria-label="Startmonat"
                          />
                        </div>
                      </label>
                      <label className="flex flex-col gap-1 text-sm">
                        Bis (Tag.Monat)
                        <div className="flex gap-2">
                          <input
                            type="number"
                            className="eh-input w-20"
                            min={1}
                            max={31}
                            disabled={!canEdit}
                            value={item.end_day ?? ""}
                            onChange={(e) =>
                              patchItem(item.id, { end_day: Number(e.target.value) || null })
                            }
                            aria-label="Endtag"
                          />
                          <input
                            type="number"
                            className="eh-input w-20"
                            min={1}
                            max={12}
                            disabled={!canEdit}
                            value={item.end_month ?? ""}
                            onChange={(e) =>
                              patchItem(item.id, { end_month: Number(e.target.value) || null })
                            }
                            aria-label="Endmonat"
                          />
                        </div>
                      </label>
                    </div>
                  ) : null}
                </fieldset>

                {canEdit ? (
                  <div className="flex justify-end">
                    <Button type="button" variant="secondary" onClick={() => removeItem(item.id)}>
                      Meldung entfernen
                    </Button>
                  </div>
                ) : null}
              </div>
            </Card>
            );
          })}

          {items.length === 0 ? (
            <p className="text-sm text-label-secondary">Noch keine Meldungen angelegt.</p>
          ) : null}
        </div>
      )}

      {canEdit ? (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={() => setItems((prev) => [...prev, newWebsiteNewsItem("always")])}>
            Meldung hinzufügen
          </Button>
          <Button type="button" variant="secondary" onClick={() => setItems((prev) => [...prev, newWebsiteNewsItem("recurring")])}>
            Jährliche Meldung hinzufügen
          </Button>
          <Button type="button" className="ml-auto" disabled={saving} onClick={() => void onSave()}>
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
